﻿<script setup lang="ts">
    import { ref, onMounted, inject } from 'vue';
    import { useRouter } from 'vue-router';
    import { FormListItem, loadForms, WidgetConfiguration, loadWidgetConfiguration, saveWidgetConfiguration } from '@views/Web/Forms.access';
    import { parse } from '@views/Web/WidgetService'; //shoul dbe { parse, widgetRuleset } but rollup doesnt understand
    import RuleEditor from '@views/Web/FormsList/RuleEditor.vue';
    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import List from '@shared/components/List.vue';
    import { copyToClipboard } from '@views/Web/copyToClipboard';
    import { bsAlert } from '@shared/components/bsAlerts';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ScopeAccess, vRequireScopeParams } from '@shared/directives/Scope.access';
    import { Permissions } from '@shared/Root/Permissions';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);

    let router = useRouter();

    const newForm = function () {
        router.push({ name: 'FormsNew' });
    };

    let formsList = ref<FormListItem[]>([]);
    let widgetConfiguration = ref<WidgetConfiguration | null>(null);
    let widgetRules: any | null = null; //should be widget ruleset but rollup doesnt understand
    let isLoading = ref(true);
    let isSaving = ref(false);
    let current = ref(-1);
    let currentFormsRules = ref<string>('');
    let hasChanges = ref<boolean>(false);
    let hasWidgetExecutableScope = false;

    onMounted(async () => {
        formsList.value = await loadForms();

        await loadWidgetConfiguration().then((wc: any) => {
            let data = wc.Data ?? '{"rules": []}';
            widgetConfiguration.value = wc;
            widgetRules = parse(data);
            currentFormsRules.value = widgetRules.serialize();
            isLoading.value = false;
        });
    });

    const requireScopeParams: vRequireScopeParams = {
        Feature: 'Widgets',
        Permission: Permissions.Execute,
    };

    hasWidgetExecutableScope = scopeAccess.hasScope(requireScopeParams);

    const saveChanges = (publish: boolean = false) => {
        if (isSaving.value) return;
        isSaving.value = true;

        let json = widgetRules?.serialize();
        let wc = widgetConfiguration.value;

        if (wc != null) {
            wc.Data = json ?? '';
            saveWidgetConfiguration(json ?? '', publish).then((newConfiguration: any) => {
                isSaving.value = false;
                widgetRules && (currentFormsRules.value = widgetRules.serialize());
                widgetConfiguration.value = newConfiguration;
                bsAlert({ title: $Resources.FormsSaveAndPublishAlertHeading, content: $Resources.FormsSaveAndPublishAlertBody });
            });
        } else {
            alert('debug, shold not happen, remove at pr');
        }

        current = ref(-1);
    };

    const copyForm = (webContentId: string) => {
        router.push({ name: 'FormsCopy', params: { copyFromWebContentId: webContentId } });
    };
</script>
<template>
    <div class="p-lg-r p-lg-l">
        <h1>
            {{ $Resources.FormsHeading }}
            <small>{{ formsList.length }} {{ $Resources.FormsCreatedLabel }}</small>
        </h1>

        <div v-if="widgetConfiguration != null" v-require-scope="{ Feature: 'Widgets', Permission: Permissions.Read }">
            <div class="d-flex flex-wrap flex-even gap-lg">
                <BSFormInputText
                    class="form-group"
                    :modelValue="widgetConfiguration.TestWidgetPath ?? ''"
                    :label="$Resources.FormsWidgetScriptTestLabel"
                    :disabled="true">
                    <template #button>
                        <button class="btn btn-default" type="button" @click="copyToClipboard(widgetConfiguration?.TestWidgetPath as string)">
                            {{ $Resources.CopyBtn }}
                        </button>
                    </template>
                </BSFormInputText>

                <BSFormInputText
                    class="form-group"
                    :modelValue="widgetConfiguration.WidgetPath ?? ''"
                    :label="$Resources.FormsWidgetScriptMainLabel"
                    :disabled="true">
                    <template #button>
                        <button class="btn btn-default" type="button" @click="copyToClipboard(widgetConfiguration?.WidgetPath as string)">
                            {{ $Resources.CopyBtn }}
                        </button>
                    </template>
                </BSFormInputText>
            </div>

            <div class="alert alert-warning alert-fixed-i" v-if="widgetConfiguration.HasUnpublishedChanges && hasWidgetExecutableScope">
                <p>
                    <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                    <strong>{{ $Resources.FormsAlertUnpublishedChangesHeading }}</strong>
                    {{ $Resources.FormsAlertUnpublishedChangesBody }}
                </p>
                <div class="form-group">
                    <button type="button" class="btn btn-warning" @click="saveChanges(true)">
                        {{ $Resources.FormsAlertUnpublishedChangesBtn }}
                    </button>
                </div>
            </div>

            <div class="alert alert-warning alert-fixed-i" v-if="widgetConfiguration.HasUnpublishedChanges && !hasWidgetExecutableScope">
                <p>
                    <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                    <strong>{{ $Resources.FormsAlertUnpublishedChangesNoPermissionHeading }}</strong>
                    {{ $Resources.FormsAlertUnpublishedChangesNoPermissionBody }}
                </p>
            </div>
        </div>
    </div>

    <List :items="formsList" :loading="isLoading" :filterKeys="['Name']">
        <template #toolbar>
            <div class="m-auto-l d-flex flex-wrap gap-sm">
                <button @click="newForm" class="btn btn-primary" v-show="current === -1 && !isSaving">{{ $Resources.FormsCreateNewBtn }}</button>

                <button class="btn btn-subtle" type="button" v-show="current > -1 || isSaving" @click="current = -1">
                    {{ $Resources.FormsCancelBtn }}
                </button>

                <button
                    class="btn btn-primary"
                    type="button"
                    v-show="current > -1 || isSaving"
                    @click="saveChanges(false)"
                    :disabled="!hasChanges"
                    :class="{ 'is-busy': isSaving }">
                    {{ $Resources.FormsSaveAndPublishBtn }}
                </button>
            </div>
        </template>
        <template #item="{ item, index }">
            <div class="flex-1" style="min-width: min(400px, 100%)">
                <h4>
                    <RouterLink :to="{ name: 'FormsEditor', params: { webContentId: item.WebContentId } }">
                        {{ item.Name && item.Name.trim() ? item.Name : $Resources.FormsListNoNamePlaceholder }}
                    </RouterLink>
                </h4>
                <p v-show="item.Description">{{ item.Description }}</p>
            </div>
            <div style="align-self: center">
                <div v-if="index === current" class="m-xs-b">
                    <rule-editor
                        :currentFormsRules="currentFormsRules"
                        @update:rules="(newValue) => (hasChanges = newValue)"
                        :form="item as FormListItem"
                        :widgetRuleset="widgetRules"></rule-editor>
                </div>
                <div class="m-xs-b" v-else v-require-scope="{ Feature: 'Widgets', Permission: Permissions.Write }">
                    <button type="button" class="btn btn-default" @click="current = index">{{ $Resources.FormsEditRulesBtn }}</button>
                </div>
            </div>
            <div style="align-self: center">
                <div class="m-xs-b">
                    <button type="button" class="btn btn-subtle" @click="copyForm(item.WebContentId)">
                        {{ $Resources.FormsEditCopyBtn }}
                    </button>
                </div>
            </div>
        </template>
    </List>
</template>

<style scoped></style>
